import { FlexColumn, FlexRow, Loading, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Modal, ModalBody, ModalHeader } from 'baseui/modal';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import { getDevice } from '../../../services/deviceService';
import { selectDeviceState } from '../../../storage/devices';
import { Device } from '../../../types/device';
import StyledTooltip from '../../ui/StyledTooltip';
import { DeviceForm } from '../DeviceForm';
import { DeviceInfo } from '../DeviceInfo';

type Props = {
  isOpen: boolean;
  selectedDevice?: Device;
  onClose: (update?: boolean) => void;
};
function DeviceDetailModal({ onClose, isOpen, selectedDevice }: Props) {
  const { css, theme } = useStyles();
  const [device, setDevice] = useRecoilState(selectDeviceState);
  const [editingMode, setEditingMode] = useState(false);
  const { startLoading, stopLoading, loading } = useLoading();

  useEffect(() => {
    if (isOpen && selectedDevice) setEditingMode(false);
  }, [isOpen]);

  useEffect(() => {
    if (!selectedDevice) {
      setDevice(undefined);
      setEditingMode(true);
      return;
    }
    const loadDevice = async () => {
      startLoading();
      const { device, error } = await getDevice(selectedDevice._id);
      if (!error && device) {
        setDevice(device);
      } else {
        setDevice(undefined);
      }
      stopLoading();
    };
    loadDevice();
  }, [selectedDevice]);

  return createPortal(
    <Modal
      animate
      closeable
      onClose={() => onClose()}
      isOpen={isOpen}
      size="auto"
      overrides={{
        Root: {
          style: {
            overflow: 'hidden',
          },
        },
        Dialog: {
          style: {
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {loading && (
        <FlexColumn
          centered
          classNames={css({
            height: '400px',
            width: '400px',
          })}
        >
          <Loading />
        </FlexColumn>
      )}
      {!loading && (
        <>
          <FlexRow
            gap={0}
            classNames={css({
              justifyContent: 'space-between',
              margin: '6px 2rem 0px 0px',
            })}
          >
            <ModalHeader
              className={css({
                marginBottom: '0 !important',
              })}
            >
              {device?.ident ?? 'Nuevo'}
            </ModalHeader>
            {!editingMode && (
              <StyledTooltip
                content={'Editar'}
                showArrow={false}
              >
                <Button
                  size="compact"
                  shape="square"
                  onClick={() => setEditingMode(true)}
                  kind="tertiary"
                >
                  <MaterialIcon
                    name={'edit'}
                    size="xs-mini"
                  />
                </Button>
              </StyledTooltip>
            )}
          </FlexRow>
          <ModalBody
            className={css({
              borderTop: '1px solid rgb(226, 226, 226)',
              boxSizing: 'border-box',
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              maxHeight: '85vh',
              maxWidth: '1200px',
              minWidth: '350px',
              overflow: 'auto',
              padding: theme.sizing.scale600,
              width: '75vw',
            })}
          >
            {!editingMode && device && <DeviceInfo device={device} />}
            {editingMode && (
              <DeviceForm
                device={device}
                onCancel={() => setEditingMode(false)}
                afterSave={() => onClose(true)}
              />
            )}
          </ModalBody>
        </>
      )}
    </Modal>,
    document.body,
  );
}

export default memo(DeviceDetailModal);
